import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
  QueryClient,
} from '@tanstack/react-query';
import { useImproveAxios } from '@/app2/shared/hooks/useImproveAxios';
import { getImproveAPI } from '../gen';
import { USERS_QUERY_KEYS } from './users';
import { QUERY_KEYS } from './user';
import type {
  ListPlaylistsResponse,
  PlaylistDetailDto,
  CreatePlaylistBody,
  CreatePlaylistResponse,
  UpdatePlaylistBody,
  UpdatePlaylistResponse,
  PlaylistsControllerListPlaylistsParams,
  PlaylistListItemDto,
  PlaylistCourseDto,
  BulkEnrollPlaylistBody,
  BulkUnenrollPlaylistBody,
  PostEnrollUsersInPlaylistInProgressResponse,
  PostUnenrollUsersInPlaylistInProgressResponse,
  EnrollCohortInPlaylistBody,
  EnrollCohortInPlaylistResponse,
  BulkCompletePlaylistBody,
  BulkCompletePlaylistResponse,
  PlaylistCertificationDto as GeneratedPlaylistCertificationDto,
} from '../gen';

// Query keys
export const PLAYLIST_QUERY_KEYS = {
  LIST: 'playlists-list',
  DETAIL: 'playlist-detail',
} as const;

/**
 * Prefix for `useListPlaylistEnrollments` query keys (see repos/courses.ts).
 * Invalidate with `[...PLAYLIST_ENROLLMENTS_QUERY_PREFIX, playlistId]` to refresh any cohort/filter variant.
 */
export const PLAYLIST_ENROLLMENTS_QUERY_PREFIX = ['playlists', 'enrollments'] as const;

function invalidateAfterPlaylistRosterChange(
  queryClient: QueryClient,
  playlistId: number,
  affectedUserIds?: readonly number[],
): void {
  queryClient.invalidateQueries({ queryKey: [PLAYLIST_QUERY_KEYS.LIST] });
  queryClient.invalidateQueries({ queryKey: [PLAYLIST_QUERY_KEYS.DETAIL, playlistId] });
  queryClient.invalidateQueries({ queryKey: [...PLAYLIST_ENROLLMENTS_QUERY_PREFIX, playlistId] });
  // Playlist enrollment fans out to course enrollments; refresh any open course enrollment grids.
  queryClient.invalidateQueries({ queryKey: ['courses', 'enrollments'] });

  const invalidateAdminUserCaches = (userId: number) => {
    queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEYS.USER_FEED, userId] });
    queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEYS.USER_ACTIVE_COURSES, userId] });
    queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEYS.USER_OVERDUE_LESSONS, userId] });
    queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEYS.USER_RECENT_ACTIVITY, userId] });
  };

  if (affectedUserIds?.length) {
    for (const userId of affectedUserIds) {
      invalidateAdminUserCaches(userId);
    }
  } else {
    queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEYS.USER_FEED] });
    queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEYS.USER_ACTIVE_COURSES] });
    queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEYS.USER_OVERDUE_LESSONS] });
    queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEYS.USER_RECENT_ACTIVITY] });
  }

  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FEED] });
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ENROLLED_COURSES] });
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ACTIVE_COURSES] });
}

// Re-export types for convenience
export type {
  ListPlaylistsResponse,
  PlaylistDetailDto,
  CreatePlaylistBody,
  UpdatePlaylistBody,
  PlaylistListItemDto,
  PlaylistCourseDto,
  BulkEnrollPlaylistBody,
  BulkUnenrollPlaylistBody,
};

// Type aliases for backwards compatibility
export type PlaylistDetail = PlaylistDetailDto;
export type PlaylistListItem = PlaylistListItemDto;
export type PlaylistCourse = PlaylistCourseDto;

/** OpenAPI-generated list params (includes `filters` for title filters). */
export type ListPlaylistsParams = PlaylistsControllerListPlaylistsParams;

export type { PlaylistAttributeFilterDto, PlaylistFiltersDto } from '../gen';

export type BulkEnrollPlaylistParams = BulkEnrollPlaylistBody;
export type BulkUnenrollPlaylistParams = BulkUnenrollPlaylistBody;

/**
 * Hook to list playlists with pagination and filtering
 */
export function useListPlaylists(
  params: ListPlaylistsParams = {},
): UseQueryResult<ListPlaylistsResponse> {
  const axiosConfig = useImproveAxios();
  const { playlistsControllerListPlaylists } = getImproveAPI();

  return useQuery<ListPlaylistsResponse>({
    queryKey: [PLAYLIST_QUERY_KEYS.LIST, params],
    queryFn: async () => {
      const response = await playlistsControllerListPlaylists(params as PlaylistsControllerListPlaylistsParams, {
        ...axiosConfig,
        paramsSerializer: {
          serialize: (requestParams: Record<string, unknown>) => {
            if (!requestParams) return '';
            const { filters, ...rest } = requestParams;
            const pairs: string[] = [];
            for (const [key, value] of Object.entries(rest)) {
              if (value !== undefined && value !== null) {
                pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
              }
            }
            if (filters !== undefined && filters !== null) {
              pairs.push(`filters=${encodeURIComponent(JSON.stringify(filters))}`);
            }
            return pairs.join('&');
          },
        },
      });
      return response.data;
    },
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to get a single playlist by ID
 */
export function useGetPlaylist(
  playlistId: number | undefined,
): UseQueryResult<PlaylistDetail> {
  const axiosConfig = useImproveAxios();
  const { playlistsControllerGetPlaylist } = getImproveAPI();

  return useQuery<PlaylistDetail>({
    queryKey: [PLAYLIST_QUERY_KEYS.DETAIL, playlistId],
    queryFn: async () => {
      if (!playlistId) throw new Error('Playlist ID is required');
      const response = await playlistsControllerGetPlaylist(playlistId, axiosConfig);
      return response.data;
    },
    enabled: !!playlistId,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to create a new playlist
 */
export function useCreatePlaylist(): UseMutationResult<CreatePlaylistResponse, Error, CreatePlaylistBody> {
  const queryClient = useQueryClient();
  const axiosConfig = useImproveAxios();
  const { playlistsControllerCreatePlaylist } = getImproveAPI();

  return useMutation<CreatePlaylistResponse, Error, CreatePlaylistBody>({
    mutationFn: async (body) => {
      const response = await playlistsControllerCreatePlaylist(body, axiosConfig);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PLAYLIST_QUERY_KEYS.LIST] });
    },
  });
}

/**
 * Hook to update a playlist
 */
export function useUpdatePlaylist(
  playlistId: number,
): UseMutationResult<UpdatePlaylistResponse, Error, UpdatePlaylistBody> {
  const queryClient = useQueryClient();
  const axiosConfig = useImproveAxios();
  const { playlistsControllerUpdatePlaylist } = getImproveAPI();

  return useMutation<UpdatePlaylistResponse, Error, UpdatePlaylistBody>({
    mutationFn: async (body) => {
      const response = await playlistsControllerUpdatePlaylist(playlistId, body, axiosConfig);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PLAYLIST_QUERY_KEYS.LIST] });
      queryClient.invalidateQueries({ queryKey: [PLAYLIST_QUERY_KEYS.DETAIL, playlistId] });
    },
  });
}

/**
 * Hook to delete a playlist
 */
export function useDeletePlaylist(): UseMutationResult<void, Error, number> {
  const queryClient = useQueryClient();
  const axiosConfig = useImproveAxios();
  const { playlistsControllerDeletePlaylist } = getImproveAPI();

  return useMutation<void, Error, number>({
    mutationFn: async (playlistId) => {
      await playlistsControllerDeletePlaylist(playlistId, axiosConfig);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PLAYLIST_QUERY_KEYS.LIST] });
    },
  });
}

// Bulk enrollment hooks for playlists
/**
 * Hook to bulk enroll users in a playlist
 */
export function useBulkEnrollUsersInPlaylist(
  playlistId: number,
): UseMutationResult<PostEnrollUsersInPlaylistInProgressResponse, Error, BulkEnrollPlaylistBody> {
  const queryClient = useQueryClient();
  const axiosConfig = useImproveAxios();
  const { playlistEnrollmentsControllerBulkEnroll } = getImproveAPI();

  return useMutation<PostEnrollUsersInPlaylistInProgressResponse, Error, BulkEnrollPlaylistBody>({
    mutationFn: async (body) => {
      const response = await playlistEnrollmentsControllerBulkEnroll(playlistId, body, axiosConfig);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      invalidateAfterPlaylistRosterChange(queryClient, playlistId, variables.userIds);
    },
  });
}

/**
 * Hook to bulk unenroll users from a playlist
 */
export function useBulkUnenrollUsersFromPlaylist(
  playlistId: number,
): UseMutationResult<PostUnenrollUsersInPlaylistInProgressResponse, Error, BulkUnenrollPlaylistBody> {
  const queryClient = useQueryClient();
  const axiosConfig = useImproveAxios();
  const { playlistEnrollmentsControllerBulkUnenroll } = getImproveAPI();

  return useMutation<PostUnenrollUsersInPlaylistInProgressResponse, Error, BulkUnenrollPlaylistBody>({
    mutationFn: async (body) => {
      const response = await playlistEnrollmentsControllerBulkUnenroll(playlistId, body, axiosConfig);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      invalidateAfterPlaylistRosterChange(queryClient, playlistId, variables.userIds);
    },
  });
}

// ============================================================================
// Certification
// ============================================================================

export type PlaylistCertificationDto = GeneratedPlaylistCertificationDto;

/**
 * Hook to get the certification associated with a playlist.
 * Calls GET /api/_internal/playlists/:playlistId/certification
 */
export function useGetPlaylistCertification(
  playlistId: number | undefined,
): UseQueryResult<PlaylistCertificationDto> {
  const axiosConfig = useImproveAxios();
  const { playlistsControllerGetPlaylistCertification } = getImproveAPI();

  return useQuery<PlaylistCertificationDto>({
    queryKey: [PLAYLIST_QUERY_KEYS.DETAIL, playlistId, 'certification'],
    queryFn: async () => {
      if (!playlistId) throw new Error('Playlist ID is required');
      const response = await playlistsControllerGetPlaylistCertification(playlistId, axiosConfig);
      return response.data;
    },
    enabled: !!playlistId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Enroll cohort in playlist: enroll active users in the given cohort (async).
 * Returns immediately; completion is notified via WebSocket. Use ManageBulkActionStatusContext to show progress snackbar.
 */
export function useEnrollCohortInPlaylist(
  playlistId: number,
): UseMutationResult<EnrollCohortInPlaylistResponse, Error, EnrollCohortInPlaylistBody> {
  const queryClient = useQueryClient();
  const axiosConfig = useImproveAxios();
  const { playlistEnrollmentsControllerEnrollCohort } = getImproveAPI();

  return useMutation<EnrollCohortInPlaylistResponse, Error, EnrollCohortInPlaylistBody>({
    mutationFn: async (params) => {
      const response = await playlistEnrollmentsControllerEnrollCohort(playlistId, params, axiosConfig);
      return response.data;
    },
    onSuccess: (data) => {
      if (!data.inProgress) {
        invalidateAfterPlaylistRosterChange(queryClient, playlistId);
      }
    },
  });
}

/**
 * Bulk complete all lessons in a playlist for specified users (async).
 * Returns immediately; completion is notified via WebSocket.
 */
export function useBulkCompletePlaylistEnrollments(
  playlistId: number,
): UseMutationResult<BulkCompletePlaylistResponse, Error, BulkCompletePlaylistBody> {
  const queryClient = useQueryClient();
  const axiosConfig = useImproveAxios();
  const { playlistEnrollmentsControllerBulkComplete } = getImproveAPI();

  return useMutation<BulkCompletePlaylistResponse, Error, BulkCompletePlaylistBody>({
    mutationFn: async (body) => {
      const response = await playlistEnrollmentsControllerBulkComplete(playlistId, body, axiosConfig);
      return response.data;
    },
    onSuccess: (data) => {
      if (!data.inProgress) {
        invalidateAfterPlaylistRosterChange(queryClient, playlistId);
      }
    },
  });
}
